import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site";

const ELIGIBILITY = [
  "Adults and young people seeking to build practical, social or emotional skills",
  "Voluntary, involuntary and mandated participants — all welcome",
  "Participants on bail conditions or other justice-system orders",
  "Flexible to participant capacity, schedule, and goals",
  "NDIS, justice-funded, private and self-funded pathways accepted",
];

const PROGRAMS = [
  { w: "Practical skills", t: "Group Cooking Classes", b: "Hands-on cooking sessions building practical life skills, healthy habits and social confidence." },
  { w: "Wellbeing", t: "Outdoor Recreational Activities", b: "Bushwalks, group adventures and structured outdoor activity that regulate, restore and build community." },
  { w: "Mental capacity", t: "One-on-One Counselling", b: "Personalised counselling with an ACA-registered counsellor — a confidential space to build skills." },
  { w: "Shared learning", t: "Group Counselling", b: "Facilitated group sessions building emotional regulation, communication, and the capacity to belong." },
];

const STEPS = [
  ["01", "Submit referral", "Use the online form or call our intake line. Acknowledged within one business day."],
  ["02", "Initial conversation", "We meet with the referrer and (where possible) the participant to understand needs and goals."],
  ["03", "Match the programs", "We recommend the program (or combination) that best supports skill-building."],
  ["04", "Begin & stay in touch", "Programs commence. You receive regular updates and remain part of the plan."],
];

export default function InfoPack() {
  useEffect(() => {
    document.title = "Neurogility — Referrer Info Pack";
  }, []);

  return (
    <div data-testid="page-info-pack" className="bg-white print:bg-white">
      {/* Screen-only toolbar */}
      <div className="print:hidden bg-warm-50 border-b border-warm-200/60">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/professionals" className="inline-flex items-center gap-2 text-ink-700 hover:text-brand text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to For Professionals
          </Link>
          <button
            data-testid="print-info-pack"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors"
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Pack content */}
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-12 print:py-6 print:px-8">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-ink-900/10 pb-6 mb-8">
          <div className="flex items-center gap-3">
            <img
              src={SITE.logo}
              alt={`${SITE.name} logo`}
              className="h-12 w-12 rounded-lg object-cover"
            />
            <div>
              <p className="font-serif text-2xl tracking-tight text-ink-900 leading-none">Neurogility</p>
              <p className="text-xs uppercase tracking-widest text-ink-400 mt-1">Referrer Information Pack</p>
            </div>
          </div>
          <p className="text-xs text-ink-400 text-right">
            {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long" })}
          </p>
        </header>

        {/* Hero */}
        <section className="mb-10">
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight text-ink-900 mb-4">
            A Melbourne community services organisation helping people build skills for a better life.
          </h1>
          <p className="text-lg text-ink-700 leading-relaxed">
            Neurogility delivers holistic, skill-focused programs to adults and young people — including
            those who are mandated, justice-involved, or have been resistant to support in the past.
            We help participants build the practical, social and emotional capabilities for the life they want.
          </p>
        </section>

        {/* At a glance */}
        <section className="mb-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["4", "Core programs"],
            ["ACA", "Registered counsellor"],
            ["1:6", "Facilitator ratio"],
            ["Mel", "Glen Waverley, VIC"],
          ].map(([v, l]) => (
            <div key={l} className="border border-ink-900/10 rounded-lg p-4">
              <p className="font-serif text-3xl text-brand">{v}</p>
              <p className="text-xs text-ink-500 mt-1 leading-tight">{l}</p>
            </div>
          ))}
        </section>

        {/* Programs */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-ink-900 mb-5 border-b border-ink-900/10 pb-2">
            Our programs
          </h2>
          <ul className="space-y-4">
            {PROGRAMS.map((p) => (
              <li key={p.t} className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <p className="text-xs uppercase tracking-widest text-brand font-semibold">{p.w}</p>
                </div>
                <div className="col-span-8">
                  <p className="font-semibold text-ink-900">{p.t}</p>
                  <p className="text-sm text-ink-700 leading-relaxed">{p.b}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Specialty */}
        <section className="mb-10 bg-warm-50 border border-warm-200/60 rounded-lg p-6">
          <h2 className="font-serif text-xl text-ink-900 mb-2">Our specialty: engagement</h2>
          <p className="text-sm text-ink-700 leading-relaxed">
            We're known for working successfully with involuntary, mandated and resistant participants —
            including people involved with the justice system. We collaborate with magistrates,
            corrections case managers and legal practitioners to support bail conditions and community-based orders.
          </p>
        </section>

        {/* Eligibility */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-ink-900 mb-5 border-b border-ink-900/10 pb-2">Eligibility</h2>
          <ul className="space-y-2">
            {ELIGIBILITY.map((e) => (
              <li key={e} className="flex items-start gap-3 text-ink-700">
                <CheckCircle2 className="w-4 h-4 text-brand shrink-0 mt-1" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Referral process */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl text-ink-900 mb-5 border-b border-ink-900/10 pb-2">
            How to refer
          </h2>
          <ol className="space-y-3">
            {STEPS.map(([n, t, b]) => (
              <li key={n} className="flex items-start gap-4">
                <span className="font-serif text-2xl text-brand w-8 shrink-0">{n}</span>
                <div>
                  <p className="font-semibold text-ink-900">{t}</p>
                  <p className="text-sm text-ink-700 leading-relaxed">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Contact */}
        <section className="border-t border-ink-900/10 pt-6">
          <h2 className="font-serif text-2xl text-ink-900 mb-4">Contact intake</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-brand mt-0.5" />
              <div>
                <p className="text-ink-400 text-xs uppercase tracking-widest">Phone</p>
                <p className="text-ink-900">{SITE.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-brand mt-0.5" />
              <div>
                <p className="text-ink-400 text-xs uppercase tracking-widest">Email</p>
                <p className="text-ink-900 break-all">{SITE.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-brand mt-0.5" />
              <div>
                <p className="text-ink-400 text-xs uppercase tracking-widest">Visit</p>
                <p className="text-ink-900">{SITE.address}</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-ink-400 mt-8 italic">
            © {new Date().getFullYear()} Neurogility · neurogility.com.au · Skills for life.
          </p>
        </section>
      </article>
    </div>
  );
}
